import React, { createContext, useState, useEffect } from 'react';
import { registerFCMToken, listenForForegroundMessages } from '../firebaseClient';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const normalizeLanguage = (value) => (value === 'en' ? 'en' : 'el');

  const getStoredLanguage = () => {
    const fromAppLang = localStorage.getItem('app_lang');
    if (fromAppLang === 'el' || fromAppLang === 'en') return fromAppLang;

    const fromAuthLang = localStorage.getItem('language');
    if (fromAuthLang === 'el' || fromAuthLang === 'en') return fromAuthLang;

    return null;
  };

  const persistLanguage = (newLang) => {
    const normalized = normalizeLanguage(newLang);
    setLanguage(normalized);
    localStorage.setItem('language', normalized);
    localStorage.setItem('app_lang', normalized);
    return normalized;
  };

  const syncLanguageToDatabase = async (newLang, tokenOverride = null) => {
    const token = tokenOverride || accessToken;
    if (!token) return;

    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/users/update-language`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ language: newLang }),
      });
    } catch (err) {
      console.error('Failed to update language in database', err);
    }
  };

  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(localStorage.getItem('accessToken') || null);
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refreshToken') || null);
  const [language, setLanguage] = useState(() => normalizeLanguage(getStoredLanguage() || 'el'));
  const [userRole, setUserRole] = useState(localStorage.getItem('userRole') || null);

  useEffect(() => {
    if (accessToken && user) {
      console.log('User session active. Negotiating Firebase Cloud Messaging bindings...');
      registerFCMToken(accessToken);
      
      const unsubscribeForeground = listenForForegroundMessages();
      
      // Clean up the foreground message subscription if the component unmounts or user changes
      return () => {
        if (typeof unsubscribeForeground === 'function') {
          unsubscribeForeground();
        }
      };
    }
  }, [accessToken, user]);
  
  //Load and validate user info whenever accessToken changes
  useEffect(() => {
     console.log('AuthContext useEffect triggered, accessToken:', accessToken, 'user:', user);
    if (accessToken) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
        .then(res => {
          // console.log('Response from /auth/me:', res);
          if (!res.ok) throw new Error('Unauthorized');
          return res.json();
        })
        .then(data => {
          //  console.log('User data fetched:', data);
            setUser(data.user);
            if (data.user.role) {
              setUserRole(data.user.role);
              localStorage.setItem('userRole', data.user.role);
            }

            const storedLanguage = getStoredLanguage();
            if (storedLanguage) {
              persistLanguage(storedLanguage);
            } else if (data.user.language) {
              persistLanguage(data.user.language);
            }

          })
        .catch(async () => {
          // console.log('Error fetching user. Trying refresh token...');
          // If the access token is expired, attempt to refresh it using the refresh token
          if (refreshToken) {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/refresh`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refreshToken }),
            });
            const data = await res.json();
            if (data.accessToken) {
              localStorage.setItem('accessToken', data.accessToken);
              setAccessToken(data.accessToken);
            } else {
              logout(); // Token refresh failed, log user out
            }
          } else {
            logout();
          }
        });
    }
  }, [accessToken]);

  const updateLanguage = async (newLang) => {
    const normalized = persistLanguage(newLang);

    // If user is logged in, sync language preference to database
    if (accessToken) {
      await syncLanguageToDatabase(normalized, accessToken);
    }
  };

  // Save authentication tokens to localStorage and state
  const login = async (newAccessToken, newRefreshToken) => {
    const preferredLang = getStoredLanguage() || 'el';
    localStorage.setItem('accessToken', newAccessToken);
    localStorage.setItem('refreshToken', newRefreshToken);
    setAccessToken(newAccessToken);
    setRefreshToken(newRefreshToken);
    persistLanguage(preferredLang);
    await syncLanguageToDatabase(preferredLang, newAccessToken);
  };

  // Clear authentication tokens and user data from localStorage and state
  const logout = async () => {
    if (refreshToken) {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
    }
    const currentLang = persistLanguage(language || getStoredLanguage() || 'el');

    // Clear all stored authentication data while preserving language preference
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userRole');
    setAccessToken(null);
    setRefreshToken(null);
    setUserRole(null);
    setUser(null);
    setLanguage(currentLang);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, accessToken, login, logout, language, updateLanguage, userRole }}>
      {children}
    </AuthContext.Provider>
  );
};
